import { useMemo, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'state'
import { useBlockNumber } from 'state/application/hooks'
import { formatDecimals, multiply, divide } from 'utils'
import moment from 'moment'
import { getApiCNRPrice, getApiInfo, getGeckoCNSPrice } from './helpers'
import { updateCnrPrice, updateCnsPrice, updateInfo } from './index'
import { CentricItemStatus } from './types'
import { State } from '../types'

export function useCentricCallback() {
  const dispatch = useAppDispatch()
  const currentBlock = useBlockNumber()
  const { cnrPrice, info, cnsPrice } = useSelector((state: State) => state.centric)

  const CNRPriceCallback = useCallback(async () => {
    dispatch(
      updateCnrPrice({
        status: CentricItemStatus.LOADING,
      }),
    )
    try {
      const { current, next } = await getApiCNRPrice()
      dispatch(
        updateCnrPrice({
          data: {
            current: String(current),
            next: String(next),
          },
          status: CentricItemStatus.FINISHED,
        }),
      )
    } catch (e) {
      dispatch(
        updateCnrPrice({
          status: CentricItemStatus.ERROR,
        }),
      )
    }
  }, [dispatch])

  const InfoCallback = useCallback(async () => {
    dispatch(
      updateInfo({
        status: CentricItemStatus.LOADING,
      }),
    )
    try {
      const { rise, swap } = await getApiInfo()
      dispatch(
        updateInfo({
          data: {
            cnrCirculating: formatDecimals(rise.circulatingSupply, 8),
            cnrBurnt: formatDecimals(rise.totalBurnt, 8),
            cnrMax: formatDecimals(rise.maximumSupply, 8),
            cnrQuarantined: formatDecimals(rise.quarantined, 8),
            cnrActive: formatDecimals(rise.activeSupply, 8),
            cnsCirculating: formatDecimals(swap.circulatingSupply, 8),
            cnsBurnt: formatDecimals(swap.totalBurnt, 8),
          },
          status: CentricItemStatus.FINISHED,
        }),
      )
    } catch (e) {
      dispatch(
        updateInfo({
          status: CentricItemStatus.ERROR,
        }),
      )
    }
  }, [dispatch])

  const CNSPriceCallback = useCallback(async () => {
    dispatch(
      updateCnsPrice({
        status: CentricItemStatus.LOADING,
      }),
    )
    try {
      const result = await getGeckoCNSPrice()
      dispatch(
        updateCnsPrice({
          data: {
            current: result['centric-cash'].usd,
          },
          status: CentricItemStatus.FINISHED,
        }),
      )
    } catch (e) {
      dispatch(
        updateCnsPrice({
          status: CentricItemStatus.ERROR,
        }),
      )
    }
  }, [dispatch])

  useEffect(() => {
    if (cnrPrice.status !== CentricItemStatus.LOADING && moment().valueOf() > cnrPrice.expiry) {
      CNRPriceCallback()
    }
  }, [currentBlock, cnrPrice, CNRPriceCallback])

  useEffect(() => {
    if (info.status !== CentricItemStatus.LOADING && moment().valueOf() > info.expiry) {
      InfoCallback()
    }
  }, [currentBlock, info, InfoCallback])

  useEffect(() => {
    if (cnsPrice.status !== CentricItemStatus.LOADING && moment().valueOf() > cnsPrice.expiry) {
      CNSPriceCallback()
    }
  }, [currentBlock, cnsPrice, CNSPriceCallback])
}

export function useCentricData() {
  const { cnrPrice, info, cnsPrice } = useSelector((state: State) => state.centric)

  return { cnrPrice, info, cnsPrice }
}

export function useCentricInfo() {
  const { data, expiry, status } = useSelector((state: State) => state.centric.info)

  const { cnrCirculating, cnrBurnt, cnrMax, cnrQuarantined, cnrActive, cnsCirculating, cnsBurnt } = data

  return { cnrCirculating, cnrBurnt, cnrMax, cnrQuarantined, cnrActive, cnsCirculating, cnsBurnt, expiry, status }
}

export function useCnrPrice() {
  const {
    data: cnrPriceData,
    status: cnrPriceStatus,
    isInitialized: cnrPriceInitialized,
  } = useSelector((state: State) => state.centric.cnrPrice)

  const {
    data: cnsPriceData,
    status: cnsPriceStatus,
    isInitialized: cnsPriceInitialized,
  } = useSelector((state: State) => state.centric.cnsPrice)

  const { current: cnrValue } = cnrPriceData
  const { current: cnsPrice } = cnsPriceData

  return useMemo(() => {
    if ((cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.READY) {
      return {
        value: undefined,
        price: undefined,
        status: CentricItemStatus.LOADING,
      }
    }
    if (
      (cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.LOADING &&
      (!cnrPriceInitialized || !cnsPriceInitialized)
    ) {
      return {
        value: undefined,
        price: undefined,
        status: CentricItemStatus.LOADING,
      }
    }
    if (
      (cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.ERROR &&
      (!cnrPriceInitialized || !cnsPriceInitialized)
    ) {
      return {
        value: undefined,
        price: undefined,
        status: CentricItemStatus.ERROR,
      }
    }

    return {
      cnrValue,
      cnrPrice: multiply(cnrValue, cnsPrice),
      status: CentricItemStatus.FINISHED,
    }
  }, [cnrValue, cnsPrice, cnrPriceStatus, cnsPriceStatus, cnrPriceInitialized, cnsPriceInitialized])
}

export function useCnrCap() {
  const {
    data: infoData,
    status: infoStatus,
    isInitialized: infoInitialized,
  } = useSelector((state: State) => state.centric.info)
  const {
    data: cnrPriceData,
    status: cnrPriceStatus,
    isInitialized: cnrPriceInitialized,
  } = useSelector((state: State) => state.centric.cnrPrice)
  const {
    data: cnsPriceData,
    status: cnsPriceStatus,
    isInitialized: cnsPriceInitialized,
  } = useSelector((state: State) => state.centric.cnsPrice)

  const { cnrCirculating, cnrActive } = infoData
  const { current: cnrValue } = cnrPriceData
  const { current: cnsPrice } = cnsPriceData

  return useMemo(() => {
    if ((infoStatus || cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.READY) {
      return {
        value: undefined,
        status: CentricItemStatus.LOADING,
      }
    }

    if (
      (infoStatus || cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.LOADING &&
      (!infoInitialized || !cnrPriceInitialized || !cnsPriceInitialized)
    ) {
      return {
        value: undefined,
        status: CentricItemStatus.LOADING,
      }
    }

    if (
      (infoStatus || cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.ERROR &&
      (!infoInitialized || !cnrPriceInitialized || !cnsPriceInitialized)
    ) {
      return {
        value: undefined,
        status: CentricItemStatus.ERROR,
      }
    }

    return {
      fullCap: multiply(multiply(cnrValue, cnsPrice), cnrCirculating),
      cnrCap: multiply(multiply(cnrValue, cnsPrice), cnrActive),
      status: CentricItemStatus.FINISHED,
    }
  }, [
    cnrValue,
    cnsPrice,
    cnrCirculating,
    cnrActive,
    infoStatus,
    cnrPriceStatus,
    cnsPriceStatus,
    infoInitialized,
    cnrPriceInitialized,
    cnsPriceInitialized,
  ])
}

export function useCnsPrice() {
  const {
    data: cnrPriceData,
    status: cnrPriceStatus,
    isInitialized: cnrPriceInitialized,
  } = useSelector((state: State) => state.centric.cnrPrice)
  const {
    data: cnsPriceData,
    status: cnsPriceStatus,
    isInitialized: cnsPriceInitialized,
  } = useSelector((state: State) => state.centric.cnsPrice)

  const { current: cnrValue } = cnrPriceData
  const { current: cnsPrice } = cnsPriceData

  return useMemo(() => {
    if ((cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.LOADING) {
      return {
        value: undefined,
        status: CentricItemStatus.LOADING,
      }
    }

    if (
      (cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.LOADING &&
      (!cnrPriceInitialized || !cnsPriceInitialized)
    ) {
      return {
        value: undefined,
        status: CentricItemStatus.LOADING,
      }
    }

    if (
      (cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.ERROR &&
      (!cnrPriceInitialized || !cnsPriceInitialized)
    ) {
      return {
        value: undefined,
        status: CentricItemStatus.ERROR,
      }
    }

    return {
      cnsValue: divide(1, cnrValue),
      cnsPrice,
      status: CentricItemStatus.FINISHED,
    }
  }, [cnrValue, cnsPrice, cnrPriceStatus, cnsPriceStatus, cnrPriceInitialized, cnsPriceInitialized])
}

export function useCnsCap() {
  const {
    data: infoData,
    status: infoStatus,
    isInitialized: infoInitialized,
  } = useSelector((state: State) => state.centric.info)
  const {
    data: cnrPriceData,
    status: cnrPriceStatus,
    isInitialized: cnrPriceInitialized,
  } = useSelector((state: State) => state.centric.cnrPrice)
  const {
    data: cnsPriceData,
    status: cnsPriceStatus,
    isInitialized: cnsPriceInitialized,
  } = useSelector((state: State) => state.centric.cnsPrice)

  const { cnsCirculating, cnrCirculating } = infoData
  const { current: cnrValue } = cnrPriceData
  const { current: cnsPrice } = cnsPriceData

  return useMemo(() => {
    if ((infoStatus || cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.READY) {
      return {
        value: undefined,
        status: CentricItemStatus.LOADING,
      }
    }

    if (
      (infoStatus || cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.LOADING &&
      (!infoInitialized || !cnrPriceInitialized || !cnsPriceInitialized)
    ) {
      return {
        value: undefined,
        status: CentricItemStatus.LOADING,
      }
    }

    if (
      (infoStatus || cnrPriceStatus || cnsPriceStatus) === CentricItemStatus.ERROR &&
      (!infoInitialized || !cnrPriceInitialized || !cnsPriceInitialized)
    ) {
      return {
        value: undefined,
        status: CentricItemStatus.ERROR,
      }
    }

    return {
      cnsMax: multiply(cnrValue, cnrCirculating),
      fullCap: multiply(multiply(cnrValue, cnsPrice), cnrCirculating),
      cnsCap: multiply(cnsPrice, cnsCirculating),
      status: CentricItemStatus.FINISHED,
    }
  }, [
    cnsPrice,
    cnsCirculating,
    cnrValue,
    cnrCirculating,
    cnrPriceStatus,
    infoStatus,
    cnsPriceStatus,
    infoInitialized,
    cnrPriceInitialized,
    cnsPriceInitialized,
  ])
}
